import { flushSync } from 'react-dom';
import type { ThemeMode } from '@/config/preferences';
import { useLayoutSettingsStore } from '@/stores/layoutSettingsStore';
import './themeTransition.css';

type Origin = { x: number; y: number };
let activeTransition: ViewTransition | undefined;
let activeAnimation: Animation | undefined;
let pendingMode: ThemeMode | undefined;
let requestId = 0;

// Ant Design site's circular reveal, without overriding component transitions.
export function setThemeWithTransition(mode: ThemeMode, origin: Origin) {
  const store = useLayoutSettingsStore.getState();
  if ((pendingMode ?? store.settings.themeMode ?? 'light') === mode) return;
  const id = ++requestId;
  activeTransition?.skipTransition();
  activeAnimation?.cancel();
  activeAnimation = undefined;
  pendingMode = mode;

  const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  const nextDark = mode === 'dark' || (mode === 'system' && systemDark);
  const root = document.documentElement;
  const sameAppearance = (root.dataset.theme === 'dark') === nextDark;
  const update = () => {
    if (id === requestId) {
      flushSync(() =>
        useLayoutSettingsStore.getState().setSettings({ themeMode: mode }),
      );
    }
  };

  if (
    sameAppearance ||
    document.visibilityState === 'hidden' ||
    !document.startViewTransition ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    activeTransition = undefined;
    pendingMode = undefined;
    delete root.dataset.themeTransition;
    update();
    return;
  }

  const x = Math.max(0, Math.min(origin.x, window.innerWidth));
  const y = Math.max(0, Math.min(origin.y, window.innerHeight));
  const radius = Math.hypot(
    Math.max(x, window.innerWidth - x),
    Math.max(y, window.innerHeight - y),
  );
  const circle = [
    `circle(0px at ${x}px ${y}px)`,
    `circle(${radius}px at ${x}px ${y}px)`,
  ];
  root.dataset.themeTransition = nextDark ? 'dark' : 'light';
  const transition = document.startViewTransition(update);
  activeTransition = transition;
  let animation: Animation | undefined;

  void transition.ready
    .then(() => {
      if (id !== requestId) return;
      animation = root.animate(
        { clipPath: nextDark ? circle : [...circle].reverse() },
        {
          duration: 500,
          easing: 'ease-in',
          // Keep the collapsed old snapshot hidden until the transition removes it.
          fill: 'forwards',
          pseudoElement: nextDark
            ? '::view-transition-new(root)'
            : '::view-transition-old(root)',
        },
      );
      activeAnimation = animation;
    })
    .catch((error: unknown) => {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        console.error('Theme transition failed', error);
      }
    });
  void transition.finished
    .finally(() => {
      // A retained clip-path would hide a snapshot in the next transition.
      animation?.cancel();
      if (activeTransition === transition) {
        activeTransition = undefined;
        activeAnimation = undefined;
        pendingMode = undefined;
        delete root.dataset.themeTransition;
      }
    })
    .catch((error: unknown) => {
      console.error('Theme update failed', error);
    });
}
